import { TRTCConnection, TRTCState } from './types';
import { State } from 'utils/state';
import { IRtcClient } from './types';
import { EConnectionEvents, ERTCEvents } from './events';
import { ISignalClient, TRawConnection } from './signal/types';
import { SignalerClient } from './signal';
import { myCameraStream } from 'state/video';
import log from 'utils/log';
import { EventEmitter, Unsubscribe } from 'utils/events';
import { SpeakerDetector } from 'utils/ui/speakerDetector';

const defaultRTCState: TRTCState = {
  me: {
    connectionId: '',
  },
  remoteConnections: {},
};

export class RTCClient extends EventEmitter implements IRtcClient {
  private static instance: RTCClient | null = null;

  private wsEndpoint: string;

  private signalerClient!: ISignalClient;

  private iceServers: RTCIceServer[];

  private published: boolean;

  private pendingCandidates: Record<string, RTCIceCandidate[]> = {};

  public store: State<TRTCState>;

  public sessionId?: string;

  private listeners: Unsubscribe[] = [];

  private speakerDetector: SpeakerDetector;

  private registerStreamRetryCount: number = 0;

  private readonly maxRegisterStreamRetries: number = 2;

  private constructor(iceServers: RTCIceServer[], wsEndpoint: string) {
    super();
    this.iceServers = iceServers;
    this.wsEndpoint = wsEndpoint;
    this.store = new State<TRTCState>(defaultRTCState);
    this.published = false;
    this.speakerDetector = new SpeakerDetector({
      threshold: 0.03,
      interval: 200,
      speakingTimeout: 2000,
    });
  }

  // Singleton instance accessor with lazy initialization
  public static getInstance(iceServers: RTCIceServer[], wsEndpoint: string): RTCClient {
    if (!RTCClient.instance || wsEndpoint !== RTCClient.instance.wsEndpoint) {
      RTCClient.instance = new RTCClient(iceServers, wsEndpoint);
    }

    return RTCClient.instance;
  }

  public async connect(): Promise<void> {
    try {
      this.signalerClient = new SignalerClient(this.wsEndpoint);
      const connection = await this.signalerClient.connect();
      this.setupListeners();
      this.store.setValues({
        me: {
          ...connection,
        },
      });
      this.emit('voip:connected', connection);
      log.log('[VOIP] Signaling Client Connected', connection, Date.now().toString().slice(-5));
      // Attempt to stream the user's camera and microphone. In most cases this will be too early and won't happen.
      this.registerOutgoingStream();
    } catch (error) {
      log.log(
        '[VOIP] Unable to connect to the stream. Destroying signaler client.',
        error,
        Date.now().toString().slice(-5)
      );
      if (this.signalerClient) this.signalerClient.disconnect();
      throw new Error('RTC Client Connection Failure');
    }
  }

  public registerOutgoingStream() {
    const stream = myCameraStream().stream;
    const me = this.store.get('me') ?? {};
    log.log('[VOIP] Registering Outgoing Stream', stream, me, Date.now().toString().slice(-5));
    if (stream) {
      this.store.setValues({
        me: {
          ...me,
          stream,
        },
      });
      this.shareMyStream();

      log.log('[VOIP] Your stream is published', Date.now().toString().slice(-5));
      // Reset retry count on successful registration
      this.registerStreamRetryCount = 0;
    } else {
      if (this.registerStreamRetryCount < this.maxRegisterStreamRetries) {
        this.registerStreamRetryCount++;
        log.log(
          `[VOIP] Retrying to register outgoing stream (attempt ${this.registerStreamRetryCount}/${this.maxRegisterStreamRetries})`,
          Date.now().toString().slice(-5)
        );
        setTimeout(this.registerOutgoingStream.bind(this), 500);
      } else {
        log.warn(
          '[VOIP] Maximum retry attempts reached for registering outgoing stream',
          Date.now().toString().slice(-5)
        );
      }
    }
  }

  registerMyVideoStreamListeners() {
    const unListenTrackAdded = myCameraStream().on('track:added', (track: MediaStreamTrack) => {
      this.addMyTrackToAllPeers(track);
    });

    const unListenTrackRemoved = myCameraStream().on('track:stopped', (track: MediaStreamTrack) => {
      this.removeMyTrackFromAllPeers(track);
    });

    this.listeners = [unListenTrackAdded, unListenTrackRemoved];
  }

  public destroy(): void {
    const { me, remoteConnections } = this.store.data;

    log.log('[VOIP] Destryoing Stream', Date.now().toString().slice(-5), remoteConnections);

    if (this.signalerClient) this.signalerClient.disconnect();

    Object.values(remoteConnections).forEach(({ peerConnection }) => {
      peerConnection?.close();
    });

    me.stream?.getTracks().forEach((track: MediaStreamTrack) => track.stop());
    this.store.setValues(defaultRTCState);

    this.listeners.forEach((unListen) => unListen());
    this.listeners = [];

    this.speakerDetector.stop();

    // Reset retry count on destroy
    this.registerStreamRetryCount = 0;
  }

  public async publish(): Promise<void> {
    this.published = true;
    const remoteConnections = this.store.get('remoteConnections');

    log.log('[VOIP] Publishing Stream', Date.now().toString().slice(-5), remoteConnections);

    Object.keys(remoteConnections).forEach((connectionId) => {
      this.prepareConnectionAndOffer(connectionId);
    });

    this.speakerDetector.start();
  }

  private setupListeners(): void {
    this.signalerClient.on(EConnectionEvents.ConnectionAdded, ({ connectionId, meta }: TRawConnection) => {
      log.log('[VOIP] Connection Adding - ', connectionId, Date.now().toString().slice(-5));
      const remoteConnections = this.store.get('remoteConnections');
      const peerConnection = this.createPeerConnection(connectionId);
      this.store.setValues({
        remoteConnections: {
          ...remoteConnections,
          [connectionId]: { connectionId, meta, peerConnection },
        },
      });
      if (this.published) this.prepareConnectionAndOffer(connectionId);
      this.emit('peer:connected', connectionId, this.store.get('remoteConnections'));
      log.log('[VOIP] Connection Added - ', connectionId, Date.now().toString().slice(-5));
    });

    this.signalerClient.on(EConnectionEvents.ConnectionRemoved, ({ connectionId }: TRawConnection) => {
      this.removePeer(connectionId);
      this.emit('peer:disconnected', connectionId, this.store.get('remoteConnections'));
    });

    this.signalerClient.on(ERTCEvents.PeerSignal, this.onPeerSignal.bind(this));

    this.signalerClient.on(ERTCEvents.StreamUpdate, (data: Partial<TRTCConnection>, from?: string) => {
      const remoteConnections = this.store.get('remoteConnections');
      const remoteConnection = remoteConnections[from!];
      if (remoteConnection) {
        log.log(`[VOIP] Remote stream update - ${from}`, data);
        this.store.setValues({
          remoteConnections: {
            ...remoteConnections,
            [from!]: {
              ...remoteConnection,
              ...data,
            },
          },
        });
      }
    });

    this.speakerDetector.on('activeSpeakerChanged', (speakerId: string | null) => {
      this.handleActiveSpeakerChange(speakerId);
    });
  }

  private async onPeerSignal(data: RTCSessionDescriptionInit | RTCIceCandidate, from?: string) {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];

    if (!connection?.peerConnection) {
      console.warn('[VOIP] Peer Connection not found for ', from);
      return;
    }
    // If the data contains a `type` property, it's an SDP (offer or answer)
    if ('type' in data) {
      switch (data.type) {
        case 'offer':
          log.log('[VOIP] Received an offer from ', from, data, Date.now().toString().slice(-5));
          this.handleOfferSignals(data, from);
          break;
        case 'answer':
          log.log('[VOIP] Received an answer from ', from, data, Date.now().toString().slice(-5));
          this.handleAnswerSignals(data, from);
          break;
        default:
          console.warn('[VOIP] Received an unknown signal type from ', from, data);
      }
    } else {
      this.handleIceCandidate(data, from);
    }

    this.handlePendingIceCandidates(from);
  }

  private async handleIceCandidate(data: RTCSessionDescriptionInit | RTCIceCandidate, from?: string) {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];

    if (!connection?.peerConnection) {
      return;
    }

    // Handle ICE candidates
    if (connection.peerConnection.remoteDescription) {
      await connection.peerConnection.addIceCandidate(data as RTCIceCandidate);
    } else {
      // Queue the ICE candidate if the remote description is not set
      if (!this.pendingCandidates[from!]) {
        this.pendingCandidates[from!] = [];
      }
      this.pendingCandidates[from!].push(data as RTCIceCandidate);
    }
  }

  private async handlePendingIceCandidates(from?: string) {
    if (!from) {
      Object.keys(this.pendingCandidates).forEach((connectionId) => {
        this.handlePendingIceCandidates(connectionId);
      });
      return;
    }
    log.log('[VOIP] Handling Pending ICE Candidates', from, Date.now().toString().slice(-5));

    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];
    const candidates = this.pendingCandidates[from!];

    if (!connection?.peerConnection || !candidates.length) {
      return;
    }

    if (candidates.length && connection.peerConnection.remoteDescription) {
      candidates.forEach(async (candidate) => {
        await connection.peerConnection?.addIceCandidate(candidate);
      });
      this.pendingCandidates[from!] = [];
    }
  }

  private async handleAnswerSignals(data: RTCSessionDescriptionInit | RTCIceCandidate, from?: string) {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];

    if (!connection?.peerConnection) {
      console.warn('[VOIP] Peer Connection not found for answer from', from);
      return;
    }

    const signalingState = connection.peerConnection.signalingState;

    switch (signalingState) {
      case 'stable':
        console.warn('[VOIP] Received an answer in stable state, ignoring:', from, data);
        break;
      case 'have-local-offer':
        log.log('[VOIP] Received a have-local-offer answer from ', from, data, Date.now().toString().slice(-5));
        // Set the received SDP as the remote description for the peer connection
        await connection.peerConnection.setRemoteDescription(data as RTCSessionDescriptionInit);
        break;
      default:
        console.warn('[VOIP] Received an unknown answer signal type from ', from, data);
    }
  }

  private handleActiveSpeakerChange(speakerId: string | null): void {
    const remoteConnections = this.store.get('remoteConnections');

    // Reset all connections to not be active speakers and mark the last active speaker
    const updatedConnections = Object.keys(remoteConnections).reduce(
      (acc, connectionId) => {
        const connection = remoteConnections[connectionId];

        acc[connectionId] = {
          ...connection,
          isActiveSpeaker: connectionId === speakerId,
          lastActiveSpeaker: connection.isActiveSpeaker && !speakerId,
        };
        return acc;
      },
      {} as Record<string, TRTCConnection>
    );

    this.store.setValues({
      remoteConnections: updatedConnections,
    });
  }

  private startStreamingMe(peerConnection: RTCPeerConnection) {
    const stream = myCameraStream().stream;

    if (!stream) {
      return false;
    }

    stream.getTracks().forEach((track: MediaStreamTrack) => {
      this.sendTrackToPeer(track, peerConnection);
    });
  }

  private sendTrackToPeer(track: MediaStreamTrack, peerConnection: RTCPeerConnection) {
    if (!myCameraStream().stream) {
      return;
    }
    log.log('[VOIP] Adding track to peer connection', track, peerConnection, Date.now().toString().slice(-5));
    if (track.kind === 'audio' || track.kind === 'video') {
      const senders = peerConnection.getSenders();
      const sender = senders.find((sender) => sender.track?.kind === track.kind);
      if (!sender) {
        peerConnection.addTrack(track, myCameraStream().stream!);
      } else {
        sender.replaceTrack(track);
      }
    }
  }

  private async sendOffer(peerConnection: RTCPeerConnection, connectionId: string) {
    log.log('[VOIP] Sending Offer to ', connectionId, Date.now().toString().slice(-5));

    // Create an SDP offer to initiate the connection
    const offer = await peerConnection.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
    await peerConnection.setLocalDescription(offer);

    // Send the SDP offer to the remote peer via the signaling server
    this.signalerClient.signal({
      action: ERTCEvents.PeerSignal,
      to: [connectionId],
      data: offer,
    });
  }

  addMyTrackToAllPeers(track: MediaStreamTrack) {
    const remoteConnections = this.store.get('remoteConnections');
    Object.values(remoteConnections).forEach(({ peerConnection, connectionId }) => {
      if (peerConnection) {
        this.sendTrackToPeer(track, peerConnection);
        this.sendOffer(peerConnection, connectionId);
      }
    });
  }

  removeMyTrackFromAllPeers(track: MediaStreamTrack) {
    const remoteConnections = this.store.get('remoteConnections');
    Object.values(remoteConnections).forEach(async ({ connectionId, peerConnection }) => {
      if (!peerConnection) {
        return;
      }
      const sender = peerConnection.getSenders()?.find((sender) => sender.track === track);

      if (sender) {
        peerConnection?.removeTrack(sender);
        this.sendOffer(peerConnection, connectionId);
      }
    });
  }

  private async handleOfferSignals(data: RTCSessionDescriptionInit | RTCIceCandidate, from?: string) {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];

    if (!connection?.peerConnection) {
      console.warn('[VOIP] Peer Connection not found for offer from', from);
      return;
    }

    const signalingState = connection.peerConnection.signalingState;

    switch (signalingState) {
      case 'stable': {
        log.log('[VOIP] Received a stable offer from ', from, data, Date.now().toString().slice(-5));

        // Set the received SDP as the remote description for the peer connection
        await connection.peerConnection.setRemoteDescription(data as RTCSessionDescriptionInit);

        log.log('[VOIP] Remote Description is SET for', from, Date.now().toString().slice(-5));
        const answer = await connection.peerConnection.createAnswer();

        log.log('[VOIP] Answer created', from, answer, Date.now().toString().slice(-5));

        // Set the generated answer as the local description
        await connection.peerConnection.setLocalDescription(answer);

        log.log('[VOIP] Local Description is SET for', from, answer, Date.now().toString().slice(-5));

        this.signalerClient.signal({
          action: ERTCEvents.PeerSignal,
          to: [from!],
          data: answer,
        });

        log.log('[VOIP] Answer sent to', from, Date.now().toString().slice(-5));

        break;
      }
      default:
        console.warn('[VOIP] Received an unknown offer signal type from ', from, data);
    }
  }

  private shareMyStream(): void {
    log.log('[VOIP] Sharing my stream', Date.now().toString().slice(-5));
    const { me, remoteConnections } = this.store.data;

    Object.values(remoteConnections).forEach((remoteConnection) => {
      if (me.stream) {
        me.stream.getTracks().forEach((track: MediaStreamTrack) => {
          log.log(
            '[VOIP] Sharing my stream track with ',
            remoteConnection.connectionId,
            Date.now().toString().slice(-5)
          );

          const senders = remoteConnection.peerConnection?.getSenders();
          const senderExists = senders?.some((sender) => sender.track === track);
          if (!senderExists) {
            this.prepareConnectionAndOffer(remoteConnection.connectionId);
          } else {
            log.log(
              '[VOIP] Sender already exists for ',
              remoteConnection.connectionId,
              Date.now().toString().slice(-5)
            );
          }
        });
      }
    });

    this.registerMyVideoStreamListeners();
  }

  private createPeerConnection(connectionId: string) {
    const { remoteConnections } = this.store.data;
    const remoteConnection = remoteConnections[connectionId];
    const peerConnection = new RTCPeerConnection({ iceServers: this.iceServers });

    log.log('[VOIP] Creating Peer Connection - ', connectionId, Date.now().toString().slice(-5));

    /** Event listeners for the RTC Connection */

    // Handle ICE candidate generation
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        // Send the generated ICE candidate to the remote peer via the signaling server
        this.signalerClient.signal({
          action: ERTCEvents.PeerSignal,
          to: [connectionId],
          data: event.candidate,
        });
      }
    };

    // Handle incoming media streams from the remote peer
    peerConnection.ontrack = (event: RTCTrackEvent) => {
      const stream = event.streams[0];
      const track = event.track;
      log.log(`[VOIP] received a track from peer...`, connectionId, track);
      const remoteConnections = this.store.get('remoteConnections');
      const remoteConnection = remoteConnections[connectionId];
      if (remoteConnection) {
        this.store.setValues({
          remoteConnections: {
            ...remoteConnections,
            [connectionId]: {
              ...remoteConnection,
              stream,
            },
          },
        });
        // Add stream to speaker detector when it's an audio track
        if (track.kind === 'audio' && stream) {
          this.speakerDetector.addStream(connectionId, stream);
        }
      }
    };

    peerConnection.onnegotiationneeded = console.log;

    // Handle peer connection close event
    peerConnection.onconnectionstatechange = () => {
      if (peerConnection.iceConnectionState === 'closed') {
        log.log('[VOIP] Peer Connection Closed - ', connectionId, Date.now().toString().slice(-5));
        this.removePeer(connectionId);
        peerConnection.close();
      }
    };

    // Update the state to associate the peer connection with the remote connection.
    log.log(
      '[VOIP] Peer Connection Created - ',
      connectionId,
      'current remote connections',
      remoteConnections,
      Date.now()
    );
    this.store.setValues({
      remoteConnections: {
        ...remoteConnections,
        [connectionId]: {
          ...remoteConnection,
          peerConnection,
        },
      },
    });

    return peerConnection;
  }

  private async prepareConnectionAndOffer(connectionId: string): Promise<void> {
    const { me, remoteConnections } = this.store.data;

    if (remoteConnections && me.stream) {
      // Create a new RTCPeerConnection with the specified ICE servers
      const peerConnection = remoteConnections[connectionId]?.peerConnection || this.createPeerConnection(connectionId);

      // Add each track from the user's stream to the peer connection
      if (me.stream) {
        log.log('[VOIP] Sharing my stream to', connectionId, Date.now().toString().slice(-5));
        this.startStreamingMe(peerConnection);
        this.signalMutedState(connectionId);
      } else {
        // Reset retry count for new registration attempt
        this.registerStreamRetryCount = 0;
        this.registerOutgoingStream();
      }

      const initiator = this.getInitiator(me.connectionId, connectionId);
      const iAmTheInitiator = initiator === me.connectionId;
      log.log('[VOIP] Initiator is ', initiator, Date.now().toString().slice(-5));

      if (iAmTheInitiator) {
        this.sendOffer(peerConnection, connectionId);
      } else {
        log.log('[VOIP] Not creating offer for ', connectionId, me, Date.now().toString().slice(-5));
      }
    } else {
      log.log('[VOIP] No stream to share', Date.now().toString().slice(-5));
    }
  }
  private getInitiator = (a: string, b: string) => {
    return a > b ? a : b;
  };

  private removePeer = (connectionId: string) => {
    const remoteConnections = this.store.get('remoteConnections');
    const filteredRemoteConnections = Object.keys(remoteConnections).reduce(
      (acc: Record<string, TRTCConnection>, key: string) => {
        if (key !== connectionId) {
          acc[key] = remoteConnections[key];
        }
        return acc;
      },
      {}
    );
    this.store.setValues({ remoteConnections: filteredRemoteConnections });

    this.speakerDetector.removeStream(connectionId);
    // If no more connections, stop the detector
    if (Object.keys(filteredRemoteConnections).length === 0) {
      this.speakerDetector.stop();
    }

    log.log(
      '[VOIP] Connection Removed - ',
      connectionId,
      this.store.data.remoteConnections,
      Date.now().toString().slice(-5)
    );
  };

  private signalMutedState = (connectionId: string) => {
    this.signalerClient.signal({
      action: ERTCEvents.StreamUpdate,
      to: [connectionId],
      data: {
        audioMuted: !myCameraStream().get('isAudioEnabled'),
        videoMuted: !myCameraStream().get('isVideoEnabled'),
      },
    });
  };

  public toggleMedia = (type: 'audio' | 'video') => {
    const { me, remoteConnections } = this.store.data;
    if (me.stream) {
      switch (type) {
        case 'audio':
          myCameraStream().toggleAudio();
          break;
        case 'video':
          myCameraStream().toggleVideo();
          break;
        default:
          break;
      }
      /** Signal other peers about the stream change */
      Object.values(remoteConnections).forEach(({ connectionId }) => {
        this.signalMutedState(connectionId);
      });
    }
  };
}
